import React, { useEffect, useState, useContext } from "react";
import PropTypes from "prop-types";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableHead from '@material-ui/core/TableHead';
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableFooter from "@material-ui/core/TableFooter";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import IconButton from "@material-ui/core/IconButton";
import FirstPageIcon from "@material-ui/icons/FirstPage";
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight";
import LastPageIcon from "@material-ui/icons/LastPage";

import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";

import axios from "axios";
import { SoccerWordContext } from "./context/SoccerWordContext";

const useStyles1 = makeStyles((theme) => ({
  root: {
    flexShrink: 0,
    marginLeft: theme.spacing(2.5),
  },
}));

function TablePaginationActions(props) {
  const classes = useStyles1();
  const theme = useTheme();
  const { count, page, rowsPerPage, onChangePage } = props;

  const handleFirstPageButtonClick = (event) => {
    onChangePage(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onChangePage(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onChangePage(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onChangePage(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <div className={classes.root}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </div>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onChangePage: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const useStyles2 = makeStyles({
  table: {
    minWidth: "100%",
  },
});

const SoccerWordTable = () => {
  const classes = useStyles2();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const [state, setState] = useContext(SoccerWordContext);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    const getData = async () => {
      let params = new URLSearchParams();
      params.append("id", 0);
      params.append("lang", state.lang);
      params.append("category", state.category);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/getDataSNSword.php`,
        params
      );
      setRows(response.data);
      // setState((state) => ({ ...state, id: response.data[0].id }));
    };
    getData();
  }, [setState, state.lang, state.category]);

  const handleChangeLang = (event, val) => {
    if (val !== null) {
      setState((state) => ({ ...state, lang: val }));
      setPage(0);
    }
  };
  const handleChange = (event, val) => {
    if (val !== null) {
      setState((state) => ({ ...state, category: val }));
      setPage(0);
    }
  };

  // const handleClick = (event, id) => {
  //   setState((state) => ({ ...state, id: id }));
  // };

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  let createMarkup = function (content) {
    if (content.link_title !== null) {
      return { __html: '<a href="' + content.link_url + '">' + content.link_title + '</a>' };
    }
  };  

  return (
    <React.Fragment>
      <ToggleButtonGroup
        value={state.lang}
        exclusive
        size="small"
        onChange={handleChangeLang}
        aria-label="type"
      >
        <ToggleButton value="スペイン語" aria-label="スペイン語">
          スペイン語
        </ToggleButton>
        <ToggleButton value="イタリア語" aria-label="イタリア語">
          イタリア語
        </ToggleButton>
        <ToggleButton value="ドイツ語" aria-label="ドイツ語">
          ドイツ語
        </ToggleButton>
        <ToggleButton value="ポルトガル語" aria-label="ポルトガル語">
          ポルトガル語
        </ToggleButton>
        <ToggleButton value="オランダ語" aria-label="オランダ語">
          オランダ語
        </ToggleButton>
        <ToggleButton value="フランス語" aria-label="フランス語">
          フランス語
        </ToggleButton>
      </ToggleButtonGroup>
      <ToggleButtonGroup
        value={state.category}
        exclusive
        size="small"
        onChange={handleChange}
        aria-label="type"
      >
        <ToggleButton value="名詞" aria-label="名詞">
          名詞
        </ToggleButton>
        <ToggleButton value="動詞" aria-label="動詞">
          動詞
        </ToggleButton>
        <ToggleButton value="形容詞" aria-label="形容詞">
          形容詞
        </ToggleButton>
        <ToggleButton value="副詞" aria-label="副詞">
          副詞
        </ToggleButton>
        <ToggleButton value="代名詞" aria-label="代名詞">
          代名詞
        </ToggleButton>
        <ToggleButton value="冠詞" aria-label="冠詞">
          冠詞
        </ToggleButton>
        <ToggleButton value="数詞" aria-label="数詞">
          数詞
        </ToggleButton>
        <ToggleButton value="前置詞" aria-label="前置詞">
          前置詞
        </ToggleButton>
        <ToggleButton value="接続詞" aria-label="接続詞">
          接続詞
        </ToggleButton>
        <ToggleButton value="疑問詞" aria-label="疑問詞">
          疑問詞
        </ToggleButton>
      </ToggleButtonGroup>
      <TableContainer component={Paper}>
        <Table
          className={classes.table}
          size="small"
          aria-label="custom pagination table"
        >
          <TableHead>
          <TableRow>
            <TableCell align="left">単語</TableCell>
            <TableCell align="left">意味</TableCell>
            <TableCell align="left">参考</TableCell>
          </TableRow>
        </TableHead>
          <TableBody>
            {(rowsPerPage > 0
              ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              : rows
            ).map((row) => (
              <TableRow
                hover
                key={row.id}
                // onClick={(event) => handleClick(event, row.id)}
              >
                <TableCell component="td" scope="row">
                  {row.title}
                </TableCell>
                <TableCell component="td" scope="row">
                  {row.meaning}
                </TableCell>
                <TableCell component="td" scope="row" style={{ width: 100, fontSize: 10 }}>
                  <div dangerouslySetInnerHTML={createMarkup(row)}></div>
                  {/* {row.link_title} */}
                </TableCell>
              </TableRow>
            ))}

            {emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={6} />
              </TableRow>
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[10, 20, { label: "All", value: -1 }]}
                colSpan={0}
                count={rows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                SelectProps={{
                  inputProps: { "aria-label": "rows per page" },
                  native: true,
                }}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
                labelRowsPerPage=""
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>{" "}
    </React.Fragment>
  );
};

export default SoccerWordTable;
